import type { CSSProperties } from 'react'

import { useStandardLayouts } from '../Designs/blocs/useRoomDesign'
import {
  type FloorFeedRef,
  useFeedsInFloor,
  useFloorState,
} from '../firebase/hooks/useFloor'
import {
  getEditingFeedIdx,
  getEditingMix,
  useEditingMixState,
  useMixes,
} from '../firebase/hooks/useMixes'
import type { Feed, IndexedElement, Layout } from '../firebase/model/mix'
import type { Floor } from '../firebase/model/floor-model'
import { copy } from '../global/utils/valtio'

type Position = {
  x: number
  y: number
  w: number
  h: number
  z?: number
  zoom?: number
  centerX?: number
  centerY?: number
}
type MediaType = 'tray' | 'camera' | 'screen' | 'mobile'

type StandardLayouts = ReturnType<typeof useStandardLayouts>

const getSideBySideFeed = (
  standardLayouts: StandardLayouts,
  feedsInFloor: FloorFeedRef[],
  type: MediaType,
  id: number
): Feed | undefined => {
  const layout = standardLayouts?.['side-by-side']

  if (!feedsInFloor.some((f) => f.id === id && f.type === type))
    return undefined

  if (!layout) return undefined

  switch (type) {
    case 'camera':
    case 'mobile': {
      const index = layout.elements.findIndex((el) => el.type === 'camera')
      if (index === -1) return undefined
      return {
        ...layout.elements[index],
        index,
        type: 'camera',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: COVER_ZOOM },
      }
    }
    case 'screen':
    case 'tray': {
      const index = layout.elements.findIndex((el) => el.type === 'media')
      if (index === -1) return undefined

      return {
        ...layout.elements[index],
        index,
        type: 'camera',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: CONTAIN_ZOOM },
      }
    }
    default:
      return undefined
  }
}

const useSideBySideFeed = (type: MediaType, id: number): Feed | undefined => {
  const standardLayouts = useStandardLayouts()
  const feedsInFloor = useFeedsInFloor()

  return getSideBySideFeed(standardLayouts, feedsInFloor, type, id)
}

const getSoloFeed = (
  standardLayouts: StandardLayouts,
  feedsInFloor: FloorFeedRef[],
  type: MediaType,
  id: number
): Feed | undefined => {
  if (
    !feedsInFloor.some(
      (f) =>
        f.id === id &&
        (f.type === type ||
          (f.id === 0 && type === 'screen' && f.type === 'tray'))
    )
  )
    return undefined

  switch (type) {
    case 'camera':
    case 'mobile': {
      const feed = standardLayouts?.['speaker-full'].elements[0]
      if (!feed) return undefined
      return {
        ...feed,
        index: 1,
        type: 'camera',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: COVER_ZOOM },
      }
    }
    case 'screen':
    case 'tray': {
      const feed = standardLayouts?.['media-full'].elements[0]
      if (!feed) return undefined
      return {
        ...feed,
        type: 'media',
        index: 1,
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: CONTAIN_ZOOM },
      }
    }
    default:
      return undefined
  }
}

const useSoloFeed = (type: MediaType, id: number): Feed | undefined => {
  const standardLayouts = useStandardLayouts()
  const feedsInFloor = useFeedsInFloor()

  return getSoloFeed(standardLayouts, feedsInFloor, type, id)
}

const usePanelLayout = (camerasCount: number, hasMedia: boolean) => {
  const standardLayouts = useStandardLayouts()

  const layoutType = hasMedia ? 'media' : 'nomedia'
  const settings = standardLayouts?.panel.settings
  const elements =
    standardLayouts?.panel.configurations[
      `${layoutType}-${camerasCount}` as keyof NonNullable<
        typeof standardLayouts
      >['panel']['configurations']
    ] ?? []

  return {
    settings,
    elements,
  }
}

const getPanelFeed = (
  standardLayouts: StandardLayouts,
  feedsInFloor: FloorFeedRef[],
  type: MediaType,
  id: number
): Feed | undefined => {
  const camerasInFloor = feedsInFloor.filter(
    (f) => f.type === 'camera' || f.type === 'mobile'
  )

  const hasMedia = feedsInFloor.some(
    (f) => f.type === 'screen' || f.type === 'tray'
  )
  const layoutType = hasMedia ? 'media' : 'nomedia'
  const layout =
    standardLayouts?.panel.configurations[
      `${layoutType}-${camerasInFloor.length}` as keyof NonNullable<
        typeof standardLayouts
      >['panel']['configurations']
    ]

  if (!layout) return undefined

  const mediaIndex = layout.findIndex((l) => l.type === 'media')
  if (type === 'tray') {
    if (feedsInFloor.find((f) => f.type === 'tray')) {
      return {
        ...layout[mediaIndex],
        index: mediaIndex,
        type: 'media',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: CONTAIN_ZOOM },
      }
    }
    return undefined
  }

  if (type === 'screen') {
    if (feedsInFloor.find((f) => f.type === type && f.id === id)) {
      return {
        ...layout[mediaIndex],
        index: mediaIndex,
        type: 'media',
        media: { type, id },
        crop: { centerX: 0, centerY: 0, zoom: CONTAIN_ZOOM },
      }
    }
    return undefined
  }

  const index = camerasInFloor.findIndex((f) => f.type === type && f.id === id)
  const speakerIndex =
    index + (mediaIndex !== -1 && mediaIndex <= index ? 1 : 0)

  const feed = layout[speakerIndex]

  return {
    ...feed,
    index: speakerIndex,
    type: 'camera',
    media: { type, id },
    crop: { centerX: 0, centerY: 0, zoom: COVER_ZOOM },
  }
}

const usePanelFeed = (type: MediaType, id: number): Feed | undefined => {
  const standardLayouts = useStandardLayouts()
  const feedsInFloor = useFeedsInFloor()

  return getPanelFeed(standardLayouts, feedsInFloor, type, id)
}

// Resolves the geometry a feed is rendered with in the hardcoded layouts.
// Pure so non-React consumers (e.g. the video priority bloc) can reuse the
// exact same boxes the floor renders with.
const getStandardLayoutFeed = (
  layout: Exclude<Floor['layout'], 'custom'>,
  standardLayouts: StandardLayouts,
  feedsInFloor: FloorFeedRef[],
  type: MediaType,
  id: number
): Feed | undefined => {
  switch (layout) {
    case 'panel':
      return getPanelFeed(standardLayouts, feedsInFloor, type, id)
    case 'side-by-side':
      return getSideBySideFeed(standardLayouts, feedsInFloor, type, id)
    case 'tray':
    case 'solo':
      return getSoloFeed(standardLayouts, feedsInFloor, type, id)
    default:
      return undefined
  }
}

const useCustomFeed = (type: MediaType, id: number) => {
  const mixes = useMixes()

  const floor = useFloorState()

  if (!floor || floor.layout !== 'custom') return undefined

  const mix = mixes.find((m) => m.id === floor.mixId)
  if (!mix) return undefined

  return mix.feeds.find(
    (f) =>
      f.media?.type === type &&
      (f.media.type === 'tray' || ('id' in f.media && f.media.id === id))
  )
}

const useMixEditingFeed = (type: MediaType, id: number) => {
  const editingMix = useEditingMixState()

  if (!editingMix?.feeds) return undefined

  return editingMix.feeds.find(
    (f) =>
      f.media?.type === type &&
      (f.media.type === 'tray' || ('id' in f.media && f.media.id === id))
  )
}

const useFeed = (type: MediaType, id: number) => {
  const floor = useFloorState()
  const editingMix = useEditingMixState()
  const editingFeed = useMixEditingFeed(type, id)
  const autoFeed = usePanelFeed(type, id)
  const customFeed = useCustomFeed(type, id)
  const soloFeed = useSoloFeed(type, id)
  const sideBySideFeed = useSideBySideFeed(type, id)

  if (editingMix) return editingFeed

  switch (floor?.layout) {
    case 'custom':
      return customFeed
    case 'panel':
      return autoFeed
    case 'side-by-side':
      return sideBySideFeed
    case 'tray':
    case 'solo':
      return soloFeed
  }
  return undefined
}

const updateFeedPosition = (updater: (feed: Feed) => Feed | undefined) => {
  const mix = getEditingMix()
  const feedIdx = getEditingFeedIdx()
  if (!mix || feedIdx === null) return

  const feed = mix.feeds[feedIdx]
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!feed) return
  const updatedFeed = updater(copy(feed))
  if (!updatedFeed) return
  mix.feeds[feedIdx] = updatedFeed
}

const useLayoutSettings = (): Layout['settings'] | undefined => {
  const floor = useFloorState()
  const editingMix = useEditingMixState()
  const mixes = useMixes()
  const standardLayouts = useStandardLayouts()

  if (editingMix) return editingMix.settings

  switch (floor?.layout) {
    case 'custom':
      return mixes.find((m) => m.id === floor.mixId)?.settings
    case 'panel':
      return standardLayouts?.panel.settings
    case 'side-by-side':
      return standardLayouts?.['side-by-side'].settings
    case 'tray':
      return standardLayouts?.['media-full'].settings
    case 'solo':
      if (floor.identifier === 'screenshare')
        return standardLayouts?.['media-full'].settings

      return standardLayouts?.['speaker-full'].settings
  }

  return undefined
}

const COVER_ZOOM = 30
const CONTAIN_ZOOM = 0

const posToStyle = (
  feed: Feed | IndexedElement | undefined,
  options?: {
    withAnchor?: boolean
  }
) => {
  if (!feed?.position) {
    return {
      // opacity: 0,
      top: '33%',
      left: '135%',
      height: '33%',
      width: '25%',
    } satisfies CSSProperties
  }

  const zIndex = feed.index
  const anchorName = options?.withAnchor ? `--slot-${feed.index}` : undefined
  return {
    left: `${feed.position.x}%`,
    top: `${feed.position.y}%`,
    width: `${feed.position.w}%`,
    height: `${feed.position.h}%`,
    zIndex,
    anchorName,
    containerType: 'size',

    '--width': feed.position.w,
    '--skew': feed.skew ? `${feed.skew ?? 0}deg` : undefined,

    '--border-radius-top-left': `${feed.radius?.topLeft ?? 0}em`,
    '--border-radius-top-right': `${feed.radius?.topRight ?? 0}em`,
    '--border-radius-bottom-left': `${feed.radius?.bottomLeft ?? 0}em`,
    '--border-radius-bottom-right': `${feed.radius?.bottomRight ?? 0}em`,

    '--outline-width': `${feed.outline?.width ?? 0}em`,
    // TODO: add support for brand token
    '--outline-color': feed.outline?.color ?? 'transparent',
  } satisfies CSSProperties
}

export {
  useFeed,
  usePanelLayout,
  useLayoutSettings,
  updateFeedPosition,
  posToStyle,
  getStandardLayoutFeed,
  type MediaType,
  type Position,
  type StandardLayouts,
  COVER_ZOOM,
  CONTAIN_ZOOM,
}
